import { type AccountError, DrawingClient, type DrawingClientError } from '@acies/platform';
import { Cache, Effect, Filter } from 'effect';
import { drawingStyleSupplement } from '../domain/drawings';
import { type ApiServices, runCall } from './runtime';
import { callDependencies, keepAnswers, viewerStylePath } from './shared';

/**
 * The statuses that mean the CDN has no such file.
 *
 * 403 and not only 404, and 403 is the one that actually happens: the bucket denies listing, so an object that is not there answers `AccessDenied` rather than `NotFound`. Reading that as absence is safe here and nowhere else — the drawing CDN is public and unauthenticated, so there is no such thing as a file we are
 * refused. 404 is listed beside it in case that ever changes.
 *
 * Written here rather than taken from the platform, because this is not what the status means in general. It is what it means for one optional file on one public origin.
 */
const ABSENT_STATUSES: ReadonlySet<number> = new Set([403, 404]);

/** How many accounts' colours to keep, which is more than a session switches between. */
const COLOR_CAPACITY = 16;

/** One, because there is one such file for everyone. */
const VIEWER_CAPACITY = 1;

/**
 * Passes only the failures that mean the file is not there, and lets every other one through.
 *
 * `composePassthrough` rather than `compose`, and that is the whole reason it is written this way: when the second filter says no, the failure the caller sees is the original `HttpClientError` rather than the reason it was narrowed to. So a 500, a schema failure or a `NetworkError` stays exactly what it was, and only
 * absence is caught.
 */
const absentFile = Filter.composePassthrough(
	Filter.reason<DrawingClientError>()('HttpClientError', 'StatusCodeError'),
	Filter.fromPredicate((reason) => ABSENT_STATUSES.has(reason.response.status)),
);

/** Everything reading the drawing stylesheets can fail with. */
export type GetDrawingStylesError = AccountError | DrawingClientError;

/**
 * Reads one stylesheet off the CDN and constructs it.
 *
 * Constructed rather than linked, so one sheet can be adopted by every viewer on the page at once and swapped without touching any of them.
 *
 * `replaceSync` cannot fail on what arrives here. CSS parsing is forgiving — an unreadable rule is dropped, not raised — and the one thing that would throw, a sheet we did not construct ourselves, is not what this is. `@import` is dropped in silence, which is exactly what should happen to the Google Fonts import in
 * `svgviewer.css`: the drawing is set in the app's own font, and a constructed sheet is not allowed to go and fetch another one.
 */
function styleSheetAt(client: typeof DrawingClient.Service, path: string): Effect.Effect<CSSStyleSheet, DrawingClientError> {
	return Effect.gen(function* () {
		const response = yield* client.get(path);
		const css = yield* response.text;
		const sheet = new CSSStyleSheet();
		sheet.replaceSync(css);
		return sheet;
	});
}

/**
 * The same, for a file that is allowed not to exist.
 *
 * Absence answers with null rather than failing, because a customer who has uploaded no colours of their own is the ordinary case and not a fault. A failure to reach the CDN at all is still a failure: it would otherwise show a drawing in the wrong colours and call that an answer.
 */
function optionalStyleSheetAt(client: typeof DrawingClient.Service, path: string): Effect.Effect<CSSStyleSheet | null, DrawingClientError> {
	return styleSheetAt(client, path).pipe(Effect.catchFilter(absentFile, () => Effect.succeed(null)));
}

/** The calls against the stylesheets a drawing is styled by. */
export interface DrawingStyleCalls {
	/**
	 * Reads everything a viewer adopts to show a drawing, in the order it adopts them.
	 *
	 * Ordered, and that is the point of answering with a list rather than with the parts: the customer's colours have to come last or they cannot override what they are there to override, and the supplement has to come after the file it supplements. A caller that assembled these itself would be a caller that could get
	 * that wrong.
	 *
	 * The customer's sheet is simply absent from the list when there is none, so a viewer adopts what it is handed without asking what each one is.
	 */
	readonly getDrawingStyles: () => Effect.Effect<ReadonlyArray<CSSStyleSheet>, GetDrawingStylesError>;
}

/** Implements the stylesheet calls over the CDN client and the path the account names. */
export function makeDrawingStyleCalls(client: typeof DrawingClient.Service, colorStylePath: Effect.Effect<string, AccountError>): DrawingStyleCalls {
	return {
		getDrawingStyles: () =>
			Effect.gen(function* () {
				const viewer = yield* styleSheetAt(client, viewerStylePath);
				const color = yield* optionalStyleSheetAt(client, yield* colorStylePath);
				return color === null ? [viewer, drawingStyleSupplement] : [viewer, drawingStyleSupplement, color];
			}),
	};
}

/**
 * The stylesheet every drawing is styled by, read once for the page.
 *
 * A cache of one rather than a value, for what a cache does that a fetch does not: every viewer created before the first answer lands waits for that one instead of asking again. A drawing section and a thumbnail row appearing together would otherwise fetch the same 13 kB twice.
 *
 * Not a layer, though it is built once and shared, because a layer is built when the runtime is — and the runtime is shared with the calculation calls. Pricing a position would then wait on a stylesheet it has no use for.
 */
const viewerSheets = Effect.runSync(
	Cache.makeWith(
		(path: string) =>
			Effect.gen(function* () {
				const client = yield* DrawingClient;
				return yield* styleSheetAt(client, path);
			}),
		{ capacity: VIEWER_CAPACITY, requireServicesAt: 'lookup', timeToLive: keepAnswers },
	),
);

/**
 * The customers' own colours, kept per account and engine.
 *
 * Keyed by the path, which names both — so switching account mid-session looks the file up again rather than showing one customer's colours on another's drawing. Absence is remembered like any other answer: an account with no colours of its own is not asked about on every drawing.
 */
const colorSheets = Effect.runSync(
	Cache.makeWith(
		(path: string) =>
			Effect.gen(function* () {
				const client = yield* DrawingClient;
				return yield* optionalStyleSheetAt(client, path);
			}),
		{ capacity: COLOR_CAPACITY, requireServicesAt: 'lookup', timeToLive: keepAnswers },
	),
);

/**
 * Forgets every stylesheet that has been read.
 *
 * Nothing in the Configurator calls this: the files do not change while a page is open, which is the whole reason they are kept. Tests do, because a cache that outlived what put things in it would answer one test with what another one arranged.
 */
export const forgetDrawingStyles = (): Promise<void> => runCall(() => Cache.invalidateAll(viewerSheets).pipe(Effect.andThen(Cache.invalidateAll(colorSheets))));

/**
 * Reads what a viewer adopts to show a drawing, from outside Effect.
 *
 * The awaitable form of {@link DrawingStyleCalls.getDrawingStyles}, for the viewer, which is a web component rather than an Effect program. A failure comes back as itself — see {@link runCall} — because a drawing whose styling could not be read must not be shown: it would be a window drawn in no colours at all, which
 * looks like an empty section rather than like a problem.
 *
 * Answers from the caches, so the files are read once for the page. That is what makes it safe for the viewer to ask for them every time it shows a drawing, which in turn is what keeps the ordering from being something a component has to remember.
 */
export const getDrawingStyles = (): Promise<GetDrawingStylesError | ReadonlyArray<CSSStyleSheet>> => runCall(() => getDrawingStylesEffect());

/**
 * Reads what a viewer adopts to show a drawing, as an Effect over the Configurator's services.
 *
 * For a caller that forks the read rather than awaiting it — see `ConfiguratorMachine`, which reads these beside the drawing they style.
 */
export const getDrawingStylesEffect = (): Effect.Effect<ReadonlyArray<CSSStyleSheet>, GetDrawingStylesError, ApiServices> =>
	callDependencies.pipe(
		Effect.flatMap(({ colorStylePath }) =>
			Effect.gen(function* () {
				const viewer = yield* Cache.get(viewerSheets, viewerStylePath);
				const color = yield* Cache.get(colorSheets, yield* colorStylePath);
				return color === null ? [viewer, drawingStyleSupplement] : [viewer, drawingStyleSupplement, color];
			}),
		),
	);
